/**
 * Bulb upsell pop-up — intercepts the main product-page "Add to basket" click
 * and offers the matching bulb before letting Horizon's own add-to-cart /
 * cart-drawer pipeline run untouched. See blocks/product-bulb-popup.liquid
 * for the full rationale and data source.
 *
 * Interception point: Horizon's add-to-cart is driven entirely by a native
 * 'submit' event on the product form (product-form.js wires it via
 * `on:submit="/handleSubmit"`, no click listener does the submission itself).
 * A single capture-phase `click` listener on `document` can therefore cancel
 * the click on the real submit button (preventDefault + stopPropagation, so
 * neither the native submit nor the theme's own button-animation click
 * handler run), show this pop-up, then resume the exact same flow with
 * `realForm.requestSubmit()` — which fires a fresh native 'submit' event
 * that this listener never sees again (no loop, no double add).
 *
 * PARE-FEUX (garde-fous) — l'ajout du luminaire au panier ne doit JAMAIS etre bloque :
 *  1. Toute erreur JS pendant l'interception -> on ne bloque pas le clic, le panier
 *     natif d'Horizon fonctionne comme sans pop-up.
 *  2. Navigateur trop ancien (pas de form.requestSubmit) -> pas d'interception.
 *  3. Pop-up sans ampoule, formulaire produit introuvable ou variante vide ->
 *     pas d'interception.
 *  4. Ajout de l'ampoule : delai max 5 s (sinon on continue sans elle) ; une erreur
 *     d'ajout (rupture, reseau) n'empeche pas l'ajout du luminaire.
 *  5. Filet de securite : si, 8 s apres le choix du client (Add / No thanks), le
 *     luminaire n'est toujours pas parti, le pop-up se ferme et l'ajout natif est
 *     lance quoi qu'il arrive.
 *  6. Le luminaire n'est soumis qu'UNE seule fois par clic (verrou).
 *  7. Touche Echap = « No thanks » (le client n'est jamais coince dans le pop-up).
 */
(() => {
  const popup = document.querySelector('[data-tlh-bulb-popup]');
  if (!popup || popup.dataset.tlhReady === '1') return;
  popup.dataset.tlhReady = '1';

  const productId = popup.dataset.productId;
  const skipEls = Array.from(popup.querySelectorAll('[data-tlh-bulb-popup-skip]'));
  const ctaBtn = popup.querySelector('[data-tlh-bulb-popup-cta]');
  const toggles = Array.from(popup.querySelectorAll('[data-tlh-bulb-toggle]'));

  // Pare-feu 3 : rien a proposer -> le script ne fait rien
  if (!productId || toggles.length === 0) return;

  const added = new Set();
  let pendingForm = null;
  let isOpen = false;
  let busy = false;
  let watchdog = null;

  // Rythme de la transition pop-up -> panier (ergonomie) :
  // 1. apres « Add », le bouton reste « Added ✓ » un court instant (confirmation visuelle) ;
  // 2. le pop-up sort en douceur (glisse vers le bas sur mobile, fondu sur ordinateur) ;
  // 3. seulement ensuite l'ajout part et le panier glisse depuis la droite (0,6 s, cart-conversion.css).
  // Reglage systeme « reduire les animations » respecte : enchainement immediat.
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const ADDED_PAUSE_MS = reduceMotion ? 0 : 450;
  const LEAVE_MS = reduceMotion ? 0 : 340;
  const BULB_ADD_TIMEOUT_MS = 5000;
  const WATCHDOG_MS = 8000;
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  function setLocked(locked) {
    skipEls.forEach((el) => {
      if (el instanceof HTMLButtonElement) el.disabled = locked;
    });
    if (ctaBtn) ctaBtn.disabled = locked;
    toggles.forEach((btn) => {
      if (!added.has(btn.dataset.bulbId)) btn.disabled = locked;
    });
  }

  function resetCards() {
    added.clear();
    setLocked(false);
    toggles.forEach((btn) => {
      btn.textContent = 'Add';
      btn.classList.remove('tlh-bulb-card__toggle--added');
      btn.disabled = false;
    });
    updateCta();
  }

  function updateCta() {
    if (!ctaBtn) return;
    if (added.size > 0) {
      ctaBtn.textContent = `Go to basket (${added.size + 1})`;
      ctaBtn.classList.add('tlh-bulb-popup__cta--filled');
    } else {
      ctaBtn.textContent = 'Skip, go to basket';
      ctaBtn.classList.remove('tlh-bulb-popup__cta--filled');
    }
  }

  function hideNow() {
    isOpen = false;
    popup.hidden = true;
    popup.classList.remove('tlh-bulb-popup--leaving');
  }

  function closePopup() {
    isOpen = false;
    if (!LEAVE_MS) {
      hideNow();
      return Promise.resolve();
    }
    popup.classList.add('tlh-bulb-popup--leaving');
    return wait(LEAVE_MS).then(hideNow);
  }

  // Soumet le luminaire UNE seule fois (pare-feu 6), quoi qu'il se passe avant.
  function submitMainForm() {
    clearTimeout(watchdog);
    const form = pendingForm;
    pendingForm = null;
    if (!form) return;
    try {
      form.requestSubmit();
    } catch (err) {
      // dernier recours : envoi classique du formulaire (page panier)
      try {
        HTMLFormElement.prototype.submit.call(form);
      } catch (_) {
        /* rien d'autre a faire */
      }
    }
  }

  // Ajoute les ampoules choisies via l'API Ajax du panier et attend la reponse.
  // Le produit principal n'est envoye qu'APRES : le panier (re-rendu par Horizon a
  // partir de cette 2e requete) s'ouvre donc toujours avec l'ampoule deja dedans.
  // Pare-feu 4 : 5 s max, et toute erreur est ignoree (le luminaire part quand meme).
  function addSelectedBulbs() {
    const items = [];
    added.forEach((id) => {
      const input = popup.querySelector(`[data-bulb-id="${id}"] input[name="id"]`);
      if (input && input.value) items.push({ id: Number(input.value), quantity: 1 });
    });
    if (!items.length) return Promise.resolve();
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const controller = 'AbortController' in window ? new AbortController() : null;
    const timer = setTimeout(() => controller && controller.abort(), BULB_ADD_TIMEOUT_MS);
    const request = fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
      signal: controller ? controller.signal : undefined,
    })
      .catch(() => {})
      .finally(() => clearTimeout(timer));
    // meme sans AbortController, on n'attend jamais plus de 5 s
    return Promise.race([request, wait(BULB_ADD_TIMEOUT_MS + 100)]);
  }

  async function proceed(pause = 0) {
    if (!isOpen || busy) return;
    busy = true;
    setLocked(true);
    // Pare-feu 5 : filet de securite, demarre au choix du client (Add / No thanks)
    clearTimeout(watchdog);
    watchdog = setTimeout(() => {
      if (pendingForm) {
        hideNow();
        submitMainForm();
        busy = false;
      }
    }, WATCHDOG_MS);
    try {
      // l'ampoule part tout de suite, pendant la confirmation + la sortie du pop-up
      const bulbsAdded = addSelectedBulbs();
      if (pause) await wait(pause);
      await closePopup();
      await bulbsAdded;
    } catch (err) {
      hideNow();
    } finally {
      submitMainForm();
      busy = false;
    }
  }

  toggles.forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.bulbId;
      if (!id || added.has(id) || busy) return;
      added.add(id);
      btn.textContent = 'Added ✓';
      btn.classList.add('tlh-bulb-card__toggle--added');
      btn.disabled = true;
      updateCta();
      // « Add » envoie directement au panier (ampoule + produit), apres un bref « Added ✓ »
      proceed(ADDED_PAUSE_MS);
    });
  });

  skipEls.forEach((el) => el.addEventListener('click', () => proceed(0)));
  if (ctaBtn) ctaBtn.addEventListener('click', () => proceed(0));

  // Pare-feu 7 : Echap = « No thanks »
  document.addEventListener('keydown', (event) => {
    if (isOpen && !busy && event.key === 'Escape') proceed(0);
  });

  document.addEventListener(
    'click',
    (event) => {
      if (isOpen || busy) return;

      // Pare-feu 1 : la moindre erreur ici -> on laisse le clic natif se faire
      try {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const button = target.closest('button[type="submit"][name="add"]');
        if (!button || button.disabled) return;
        if (button.closest('[data-tlh-bulb-popup]')) return;
        if (button.closest('#cart-drawer')) return;
        if (button.closest('.quick-add-modal')) return;

        const formComponent = button.closest('product-form-component');
        if (!formComponent || formComponent.dataset.productId !== productId) return;

        const realForm = formComponent.querySelector('form');
        // Pare-feu 2 et 3
        if (!realForm || typeof realForm.requestSubmit !== 'function') return;
        const variantInput = realForm.querySelector('input[name="id"]');
        if (!variantInput || !variantInput.value) return;

        resetCards();
        pendingForm = realForm;
        popup.hidden = false;
        isOpen = true;

        event.preventDefault();
        event.stopPropagation();
      } catch (err) {
        hideNow();
        pendingForm = null;
      }
    },
    true
  );
})();
